import { LoadingButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import { IconColor, IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import {
  FormCheckInputType,
  MultiCheckFieldVariant
} from '@aurora/shared-client/components/form/enums';
import InputEditForm from '@aurora/shared-client/components/form/InputEditForm/InputEditForm';
import type { MultiCheckFieldOption } from '@aurora/shared-client/components/form/MultiCheckField/MultiCheckField';
import ModalFormBuilder from '@aurora/shared-client/helpers/modal/ModalFormBuilder/ModalFormBuilder';
import Icons from '@aurora/shared-client/icons';
import type { Form } from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React from 'react';
import { Modal, useClassNameMapper } from 'react-bootstrap';
import useTranslation from '../../../useTranslation';
import {
  LITHIUM_COOKIES,
  LITHIUM_NECESSARY
} from '@aurora/shared-client/helpers/cookie/CookieHelper';
import formSchema from './CookiePreferences.form.json';
import localStyles from './CookiePreferencesModal.module.pcss';

interface CookieFormData {
  preferences: {
    [key: string]: boolean;
  };
  modalText: string;
}

interface Props {
  /**
   * Whether the modal is currently being displayed
   */
  show: boolean;
  /**
   * Callback function when the modal is hidden or cancelled
   *
   * @callback
   */
  onHide: () => void;
  /**
   * Callback function when user clicks submit
   *
   * @param result
   * @callback
   */
  onSubmitted: (result) => void;
  /**
   * Cookie banner privacy policy url
   */
  cookiePrivacyPolicyUrl?: string;
}

/**
 * Modal for user to choose which type of cookies can be stored
 *
 * @author Rishabh Jivan
 */
const CookiePreferencesModal: React.FC<React.PropsWithChildren<Props>> = ({
  show,
  onHide,
  onSubmitted,
  cookiePrivacyPolicyUrl
}: Props) => {
  const cx = useClassNameMapper(localStyles);
  const i18n = useTranslation(EndUserComponent.COOKIE_PREFERENCES_MODAL);
  const { formatMessage, FormattedMessage } = i18n;

  const accordionOptions: MultiCheckFieldOption<string, 'preferences', CookieFormData>[] = [];
  const defaultValues = {};
  LITHIUM_COOKIES.forEach(cookieKey => {
    accordionOptions[accordionOptions.length] = {
      name: cookieKey,
      label: formatMessage(`${cookieKey}.title`),
      description: formatMessage(`${cookieKey}.description`),
      disabled: cookieKey === LITHIUM_NECESSARY,
      headerClassName: cx('lia-banner-header'),
      disabledElement:
        cookieKey === LITHIUM_NECESSARY ? (
          <span className={cx('text-muted small')}>{formatMessage('alwaysActive')}</span>
        ) : null
    };
    defaultValues[cookieKey] = cookieKey === LITHIUM_NECESSARY;
  });

  const formSpec = new ModalFormBuilder<CookieFormData>('CookiePreferencesForm', i18n, {
    schema: formSchema as Form,
    cx
  })
    .addFieldGroupProperties('form-body')
    .addPlaceholderFormField({
      name: 'modalText',
      formGroupSpec: {
        label: false
      },
      Content: (() => {
        return (
          <>
            <FormattedMessage
              id="contentHtml"
              values={{
                p: function renderChunks(chunks): React.ReactNode {
                  return <p className={cx('lia-content-html')}>{chunks}</p>;
                }
              }}
            />
            {cookiePrivacyPolicyUrl && (
              <a
                href={cookiePrivacyPolicyUrl}
                target="_blank"
                rel="noreferrer"
                className={cx('lia-link')}
              >
                {formatMessage('cookiePolicyLink')}
                <Icon
                  icon={Icons.ExternalLinkIcon}
                  className={cx('lia-link-icon')}
                  size={IconSize.PX_14}
                  color={IconColor.LINK_COLOR}
                />
              </a>
            )}
          </>
        );
      }) as React.FC<React.PropsWithChildren<unknown>>
    })
    .addMultiCheckboxField({
      name: 'preferences',
      inputType: FormCheckInputType.SWITCH,
      variant: MultiCheckFieldVariant.ACCORDION,
      options: accordionOptions,
      fieldsetSpec: {
        legend: {
          className: cx('lia-title lia-g-mb-10')
        }
      },
      defaultValue: defaultValues
    })
    .setActionsOuterContainerClassName(cx('lia-g-locked-modal-footer'))
    .addSubmitAction({
      title: formatMessage('confirmButton'),
      buttonType: LoadingButtonVariant.LOADING_BUTTON
    })
    .build();

  /**
   * Function that handles the onSubmit action
   *
   * @param result Object containing the selected preferences from the accordion field
   */
  async function onSubmit(result): Promise<void> {
    onSubmitted(result);
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="sm"
      backdrop="static"
      centered
      aria-label="cookie-modal"
      data-testid="CookiePreferencesModal"
      className={cx('lia-g-locked-modal')}
      dialogClassName={cx('lia-g-locked-modal-dialog')}
      contentClassName={cx('lia-g-locked-modal-content')}
    >
      <Modal.Header className={cx('lia-g-locked-modal-header')} closeButton>
        <Modal.Title id="cookie-modal">{formatMessage('title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body className={cx('lia-g-locked-modal-body')}>
        <InputEditForm formSpec={formSpec} onSubmit={onSubmit} />
      </Modal.Body>
    </Modal>
  );
};

export default CookiePreferencesModal;
